import { TIER_LABEL } from '../lib/riskVocabulary';
import { useState } from 'react';
import { CatalogCsvImport, ImportCsvButton } from '../components/catalog-csv-import';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError, apiUrl } from '../api/client';
import { CompositionEditor } from '../components/composition-editor';
import { EntityEditor } from '../components/entity-editor';
import { CryptographyKbAdmin } from '../components/cryptography-kb-admin';
import { UsersAdminTab } from '../components/users-admin';
import { AuditAdminTab } from '../components/audit-admin';
import { PoliciesAdminTab } from '../components/policies-admin';
import { ModulesAdminTab } from '../components/modules-admin';
import { KbUpdatesAdminTab } from '../components/kb-updates-admin';
import { ConfirmDialog } from '../components/admin-ui';
import { useAuth } from '../auth/AuthContext';
import { AdminReadOnly, useAdminReadOnly } from '../lib/admin-read-only';
import { useEntitlementStatus, LicenseStatusBanner, ActivateScreen } from '../entitlements';

/**
 * Slice 7: Admin page — system-wide read-only views of the Threat Catalog
 * and the CRQC Probability table, plus the CRQC CSV import widget.
 *
 * Tabs (extended in Slice 9.0/9.1):
 *   - Users (9.6) — user management + roles
 *   - Audit log (9.5) — audit_event browse
 *   - Threat Catalog
 *   - CRQC Probability
 *   - Cryptography KB
 *   - Policies (9.1) — bundled compliance policies
 *   - About
 */

type AdminTab =
  | 'users' | 'audit'
  | 'threat-catalog' | 'crqc-probability' | 'cryptography-kb'
  | 'policies' | 'modules' | 'kb-updates' | 'license' | 'about';
const VALID_TABS: AdminTab[] = [
  'users', 'audit',
  'threat-catalog', 'crqc-probability', 'cryptography-kb',
  'policies', 'modules', 'kb-updates', 'license', 'about',
];

export function Admin() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const isSuperUser = hasRole('super_user');
  // Administrators see the scoring configuration read-only; super users can change it (C-024).
  const canSeeConfig = isSuperUser || isAdmin;
  const readOnly = !isSuperUser;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as AdminTab | null;
  const defaultTab = hasRole('admin') ? 'users' : 'modules';
  const tab: AdminTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : defaultTab;

  const setTab = (next: AdminTab) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', next);
    setSearchParams(sp);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      <div className="mb-2">
        <Link to="/" className="text-xs text-ink-400 hover:text-ink-700">← Home</Link>
      </div>
      <h1 className="font-display text-2xl text-ink-900 mb-1">Admin</h1>
      <p className="text-sm text-ink-500 mb-6">
        System-wide configuration. These settings apply across all projects.
      </p>

      {/* Tabs */}
      <div className="flex border-b border-ink-200 mb-6 flex-wrap">
        {isAdmin && <TabButton active={tab === 'users'} onClick={() => setTab('users')}>Users</TabButton>}
        {isAdmin && <TabButton active={tab === 'audit'} onClick={() => setTab('audit')}>Audit log</TabButton>}
        {canSeeConfig && <TabButton active={tab === 'threat-catalog'} onClick={() => setTab('threat-catalog')}>Threat Catalog</TabButton>}
        {canSeeConfig && <TabButton active={tab === 'crqc-probability'} onClick={() => setTab('crqc-probability')}>CRQC Probability</TabButton>}
        {canSeeConfig && <TabButton active={tab === 'cryptography-kb'} onClick={() => setTab('cryptography-kb')}>Cryptography KB</TabButton>}
        {canSeeConfig && <TabButton active={tab === 'policies'} onClick={() => setTab('policies')}>Policies</TabButton>}
        {canSeeConfig && <TabButton active={tab === 'modules'} onClick={() => setTab('modules')}>Modules &amp; weights</TabButton>}
        {canSeeConfig && <TabButton active={tab === 'kb-updates'} onClick={() => setTab('kb-updates')}>Knowledge base updates</TabButton>}
        {isAdmin && <TabButton active={tab === 'license'} onClick={() => setTab('license')}>License</TabButton>}
        <TabButton active={tab === 'about'} onClick={() => setTab('about')}>
          About
        </TabButton>
      </div>

      {tab === 'users' && <UsersAdminTab />}
      {tab === 'audit' && <AuditAdminTab />}
      <AdminReadOnly readOnly={readOnly && ['threat-catalog', 'crqc-probability', 'cryptography-kb', 'policies', 'modules', 'kb-updates'].includes(tab)}>
        {tab === 'threat-catalog' && <ThreatCatalogTab />}
        {tab === 'crqc-probability' && <CrqcProbabilityTab />}
        {tab === 'cryptography-kb' && <CryptographyKbAdmin />}
        {tab === 'policies' && <PoliciesAdminTab />}
        {tab === 'modules' && <ModulesAdminTab />}
        {tab === 'kb-updates' && <KbUpdatesAdminTab />}
      </AdminReadOnly>
      {tab === 'license' && <LicenseTab />}
      {tab === 'about' && <AboutTab />}
    </div>
  );
}

function TabButton({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm border-b-2 transition-colors ${
        active
          ? 'border-accent text-accent font-medium'
          : 'border-transparent text-ink-500 hover:text-ink-900'
      }`}
    >
      {children}
    </button>
  );
}

// ============================================================================
// Threat Catalog tab — sub-tabs for each entity type
// ============================================================================

type CatalogSubTab = 'compositions' | 'sources-adv' | 'sources-non-adv'
                   | 'events' | 'vulnerabilities' | 'predispos' | 'impacts';

function ThreatCatalogTab() {
  const [sub, setSub] = useState<CatalogSubTab>('compositions');

  const subTabs: { key: CatalogSubTab; label: string }[] = [
    { key: 'compositions',     label: 'Compositions' },
    { key: 'sources-adv',      label: 'Adversarial Sources' },
    { key: 'sources-non-adv',  label: 'Non-Adversarial Sources' },
    { key: 'events',           label: 'Events' },
    { key: 'vulnerabilities',  label: 'Vulnerabilities' },
    { key: 'predispos',        label: 'Predisposing Conditions' },
    { key: 'impacts',          label: 'Impacts' },
  ];

  return (
    <div>
      <div className="text-xs text-ink-500 mb-4 max-w-3xl">
        NIST SP 800-30 Rev 1 threat catalog. The entities here drive the Threat
        Analysis module's per-asset risk scoring. Add entries one at a time, or
        use Import CSV on any tab for bulk loading. System-seeded entries are
        immutable; entries you create or import are yours to edit. Compositions
        are authored individually — they join five entities and are not
        importable.
      </div>

      <div className="flex gap-1 mb-4 flex-wrap">
        {subTabs.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSub(s.key)}
            className={`text-xs px-3 py-1 rounded-sm border transition-colors ${
              sub === s.key
                ? 'bg-accent/10 border-accent/30 text-accent'
                : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {sub === 'compositions'    && <CompositionsTable />}
      {sub === 'sources-adv'     && <AdversarialSourcesTable />}
      {sub === 'sources-non-adv' && <NonAdversarialSourcesTable />}
      {sub === 'events'          && <EventsTable />}
      {sub === 'vulnerabilities' && <VulnerabilitiesTable />}
      {sub === 'predispos'       && <PredisposTable />}
      {sub === 'impacts'         && <ImpactsTable />}
    </div>
  );
}

// ----------------- Compositions table (the headline view) -----------------

interface CompositionRow {
  id: string;
  legacy_code: string | null;
  asset_type_id: string;
  asset_type_code: string;
  asset_type_name: string;
  threat_event_id: string;
  threat_event_name: string;
  vulnerability_id: string;
  vulnerability_name: string;
  vulnerability_severity: string;
  predisposing_condition_id: string;
  predisposing_condition_name: string;
  predisposing_condition_pervasiveness: string;
  impact_id: string;
  impact_name: string;
  impact_level: string;
  is_system_managed: number;
  sources?: { source_id: string; source_name: string; source_legacy_code: string | null; source_threat_level: string; source_type: 'adversarial' | 'non_adversarial' }[];
}

function CompositionsTable() {
  const readOnly = useAdminReadOnly();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['threat-catalog', 'compositions'],
    queryFn: () => api.get<{ items: CompositionRow[]; total: number }>('/threat-catalog/compositions'),
  });

  const [filter, setFilter] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CompositionRow | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<CompositionRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  function openNew() {
    setEditing(undefined);
    setEditorOpen(true);
  }
  function openEdit(row: CompositionRow) {
    setEditing(row);
    setEditorOpen(true);
  }
  async function doDelete() {
    if (!confirmDelete) return;
    setDeleteError(null);
    try {
      await api.delete(`/admin/threat-catalog/compositions/${confirmDelete.id}`);
      qc.invalidateQueries({ queryKey: ['threat-catalog', 'compositions'] });
      setConfirmDelete(null);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
      setDeleteError(msg);
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;
  if (!q.data) return null;

  const rows = q.data.items.filter((r) => {
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return (
      r.legacy_code?.toLowerCase().includes(f) ||
      r.threat_event_name.toLowerCase().includes(f) ||
      r.vulnerability_name.toLowerCase().includes(f) ||
      r.asset_type_name.toLowerCase().includes(f)
    );
  });

  // Map composition row to the editor's `existing` shape
  const editingPayload = editing ? {
    id: editing.id,
    asset_type_id: editing.asset_type_id,
    asset_type_name: editing.asset_type_name,
    threat_event_id: editing.threat_event_id,
    vulnerability_id: editing.vulnerability_id,
    predisposing_condition_id: editing.predisposing_condition_id,
    impact_id: editing.impact_id,
    impact_level: editing.impact_level,
    legacy_code: editing.legacy_code,
    sources: (editing.sources ?? []).map((s) => ({
      source_id: s.source_id,
      source_type: s.source_type,
    })),
  } : undefined;

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Filter by code, event, vulnerability, asset type..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-ink-200 rounded-sm px-3 py-1 text-sm flex-1 min-w-[200px] max-w-[480px] focus:outline-none focus:border-accent"
        />
        <div className="flex items-center gap-3">
          <div className="text-xs text-ink-500">
            {rows.length} of {q.data.total} compositions
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={openNew}
              className="text-sm px-3 py-1 bg-accent text-white border border-accent rounded-sm hover:bg-accent/90"
            >
              + New composition
            </button>
          )}
        </div>
      </div>

      <div className="border border-ink-200 rounded-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr className="text-left text-ink-500 uppercase tracking-wide text-[10px]">
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Asset Type</th>
              <th className="px-3 py-2">Threat Event</th>
              <th className="px-3 py-2">Vulnerability</th>
              <th className="px-3 py-2">Severity</th>
              <th className="px-3 py-2">Predispos. Condition</th>
              <th className="px-3 py-2">Pervasiveness</th>
              <th className="px-3 py-2">Impact</th>
              <th className="px-3 py-2">Impact Level</th>
              <th className="px-3 py-2">Sources</th>
              <th className="px-3 py-2 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-ink-100 hover:bg-ink-50/40">
                <td className="px-3 py-2 font-mono text-ink-500">{r.legacy_code ?? '—'}</td>
                <td className="px-3 py-2">{r.asset_type_name}</td>
                <td className="px-3 py-2 text-ink-700">{r.threat_event_name}</td>
                <td className="px-3 py-2 text-ink-700">{r.vulnerability_name}</td>
                <td className="px-3 py-2"><RiskInline level={r.vulnerability_severity} /></td>
                <td className="px-3 py-2 text-ink-700">{r.predisposing_condition_name}</td>
                <td className="px-3 py-2"><RiskInline level={r.predisposing_condition_pervasiveness} /></td>
                <td className="px-3 py-2 text-ink-700">{r.impact_name}</td>
                <td className="px-3 py-2"><RiskInline level={r.impact_level} /></td>
                <td className="px-3 py-2 text-[10px] text-ink-500">
                  {(r.sources ?? []).map((s) => s.source_legacy_code ?? '?').join(', ') || '—'}
                </td>
                <td className="px-3 py-2">
                  <RowActions
                    isSystem={!!r.is_system_managed}
                    onEdit={() => openEdit(r)}
                    onDelete={() => setConfirmDelete(r)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CompositionEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        existing={editingPayload}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete threat composition"
        message={
          deleteError
            ? `Failed to delete: ${deleteError}`
            : `This will permanently delete the composition${confirmDelete?.legacy_code ? ` ${confirmDelete.legacy_code}` : ''}. This cannot be undone.`
        }
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => { setConfirmDelete(null); setDeleteError(null); }}
      />
    </div>
  );
}

// ----------------- Smaller catalog tables -----------------

interface AdvSourceRow {
  id: string; legacy_code: string | null; name: string; threat_type: string;
  description: string | null; capability: string; intent: string; targeting: string;
  affiliation: string | null; geographic_location: string | null; threat_level: string;
  is_system_managed: number;
}

function AdversarialSourcesTable() {
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const q = useQuery({
    queryKey: ['threat-catalog', 'sources-adv'],
    queryFn: () => api.get<{ items: AdvSourceRow[]; total: number }>('/threat-catalog/sources/adversarial'),
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AdvSourceRow | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<AdvSourceRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  function openNew() { setEditing(undefined); setEditorOpen(true); }
  function openEdit(row: AdvSourceRow) { setEditing(row); setEditorOpen(true); }
  async function doDelete() {
    if (!confirmDelete) return;
    setDeleteError(null);
    try {
      await api.delete(`/admin/threat-catalog/sources/adversarial/${confirmDelete.id}`);
      qc.invalidateQueries({ queryKey: ['threat-catalog', 'sources-adv'] });
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;
  if (!q.data) return null;

  return (
    <>
      <CatalogCsvImport
        open={importOpen}
        onOpenChange={setImportOpen}
        entity="sources/adversarial"
        label="adversarial threat sources"
        queryKey={['threat-catalog', 'sources-adv']}
      />
      <SimpleTable
        total={q.data.total}
        headers={['Code', 'Name', 'Type', 'Capability', 'Intent', 'Targeting', 'Threat Level', 'Description', 'Actions']}
        headerCta={
          <div className="flex items-center gap-2">
            <ImportCsvButton onClick={() => setImportOpen((v) => !v)} active={importOpen} />
            <NewButton onClick={openNew} label="adversarial source" />
          </div>
        }
        rows={q.data.items.map((r) => ({
          key: r.id,
          cells: [
            <span className="font-mono text-ink-500">{r.legacy_code ?? '—'}</span>,
            <span className="text-ink-700 font-medium">{r.name}</span>,
            r.threat_type,
            <RiskInline level={r.capability} />,
            <RiskInline level={r.intent} />,
            <RiskInline level={r.targeting} />,
            <RiskInline level={r.threat_level} />,
            <span className="text-ink-500 text-[10px]">{r.description}</span>,
            <RowActions
              isSystem={!!r.is_system_managed}
              onEdit={() => openEdit(r)}
              onDelete={() => setConfirmDelete(r)}
            />,
          ],
        }))}
      />
      <EntityEditor
        kind="sources/adversarial"
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        existing={editing as ({ id: string } & Record<string, unknown>) | undefined}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete adversarial source"
        message={deleteError
          ? `Failed to delete: ${deleteError}`
          : `This will permanently delete ${confirmDelete?.name}. This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => { setConfirmDelete(null); setDeleteError(null); }}
      />
    </>
  );
}

interface NonAdvSourceRow {
  id: string; legacy_code: string | null; name: string; threat_type: string;
  description: string | null; range_of_effects: string; threat_level: string;
  is_system_managed: number;
}

function NonAdversarialSourcesTable() {
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const q = useQuery({
    queryKey: ['threat-catalog', 'sources-non-adv'],
    queryFn: () => api.get<{ items: NonAdvSourceRow[]; total: number }>('/threat-catalog/sources/non-adversarial'),
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<NonAdvSourceRow | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<NonAdvSourceRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function doDelete() {
    if (!confirmDelete) return;
    setDeleteError(null);
    try {
      await api.delete(`/admin/threat-catalog/sources/non-adversarial/${confirmDelete.id}`);
      qc.invalidateQueries({ queryKey: ['threat-catalog', 'sources-non-adv'] });
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;
  if (!q.data) return null;

  return (
    <>
      <CatalogCsvImport
        open={importOpen}
        onOpenChange={setImportOpen}
        entity="sources/non-adversarial"
        label="non-adversarial threat sources"
        queryKey={['threat-catalog', 'sources-non-adv']}
      />
      <SimpleTable
        total={q.data.total}
        headers={['Code', 'Name', 'Type', 'Range of Effects', 'Threat Level', 'Description', 'Actions']}
        headerCta={
          <div className="flex items-center gap-2">
            <ImportCsvButton onClick={() => setImportOpen((v) => !v)} active={importOpen} />
            <NewButton onClick={() => { setEditing(undefined); setEditorOpen(true); }} label="non-adversarial source" />
          </div>
        }
        rows={q.data.items.map((r) => ({
          key: r.id,
          cells: [
            <span className="font-mono text-ink-500">{r.legacy_code ?? '—'}</span>,
            <span className="text-ink-700 font-medium">{r.name}</span>,
            r.threat_type,
            <RiskInline level={r.range_of_effects} />,
            <RiskInline level={r.threat_level} />,
            <span className="text-ink-500 text-[10px]">{r.description}</span>,
            <RowActions
              isSystem={!!r.is_system_managed}
              onEdit={() => { setEditing(r); setEditorOpen(true); }}
              onDelete={() => setConfirmDelete(r)}
            />,
          ],
        }))}
      />
      <EntityEditor
        kind="sources/non-adversarial"
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        existing={editing as ({ id: string } & Record<string, unknown>) | undefined}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete non-adversarial source"
        message={deleteError
          ? `Failed to delete: ${deleteError}`
          : `This will permanently delete ${confirmDelete?.name}. This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => { setConfirmDelete(null); setDeleteError(null); }}
      />
    </>
  );
}

interface EventRow {
  id: string; legacy_code: string | null; name: string; type: string;
  sub_type: string | null; description: string | null; event_relevance: string;
  timeline: string | null; mitigating_factors: string | null;
  is_system_managed: number;
}

function EventsTable() {
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const q = useQuery({
    queryKey: ['threat-catalog', 'events'],
    queryFn: () => api.get<{ items: EventRow[]; total: number }>('/threat-catalog/events'),
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<EventRow | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<EventRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function doDelete() {
    if (!confirmDelete) return;
    setDeleteError(null);
    try {
      await api.delete(`/admin/threat-catalog/events/${confirmDelete.id}`);
      qc.invalidateQueries({ queryKey: ['threat-catalog', 'events'] });
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;
  if (!q.data) return null;

  return (
    <>
      <CatalogCsvImport
        open={importOpen}
        onOpenChange={setImportOpen}
        entity="events"
        label="threat events"
        queryKey={['threat-catalog', 'events']}
      />
      <SimpleTable
        total={q.data.total}
        headers={['Code', 'Name', 'Type', 'Sub-type', 'Relevance', 'Timeline', 'Description', 'Actions']}
        headerCta={
          <div className="flex items-center gap-2">
            <ImportCsvButton onClick={() => setImportOpen((v) => !v)} active={importOpen} />
            <NewButton onClick={() => { setEditing(undefined); setEditorOpen(true); }} label="event" />
          </div>
        }
        rows={q.data.items.map((r) => ({
          key: r.id,
          cells: [
            <span className="font-mono text-ink-500">{r.legacy_code ?? '—'}</span>,
            <span className="text-ink-700 font-medium">{r.name}</span>,
            r.type,
            r.sub_type ?? '—',
            r.event_relevance,
            r.timeline ?? '—',
            <span className="text-ink-500 text-[10px]">{r.description}</span>,
            <RowActions
              isSystem={!!r.is_system_managed}
              onEdit={() => { setEditing(r); setEditorOpen(true); }}
              onDelete={() => setConfirmDelete(r)}
            />,
          ],
        }))}
      />
      <EntityEditor
        kind="events"
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        existing={editing as ({ id: string } & Record<string, unknown>) | undefined}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete threat event"
        message={deleteError
          ? `Failed to delete: ${deleteError}`
          : `This will permanently delete ${confirmDelete?.name}. This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => { setConfirmDelete(null); setDeleteError(null); }}
      />
    </>
  );
}

interface VulnRow {
  id: string; legacy_code: string | null; name: string; type: string;
  description: string | null; severity: string;
  is_system_managed: number;
}

function VulnerabilitiesTable() {
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const q = useQuery({
    queryKey: ['threat-catalog', 'vulnerabilities'],
    queryFn: () => api.get<{ items: VulnRow[]; total: number }>('/threat-catalog/vulnerabilities'),
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<VulnRow | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<VulnRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function doDelete() {
    if (!confirmDelete) return;
    setDeleteError(null);
    try {
      await api.delete(`/admin/threat-catalog/vulnerabilities/${confirmDelete.id}`);
      qc.invalidateQueries({ queryKey: ['threat-catalog', 'vulnerabilities'] });
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;
  if (!q.data) return null;

  return (
    <>
      <CatalogCsvImport
        open={importOpen}
        onOpenChange={setImportOpen}
        entity="vulnerabilities"
        label="vulnerabilities"
        queryKey={['threat-catalog', 'vulnerabilities']}
      />
      <SimpleTable
        total={q.data.total}
        headers={['Code', 'Name', 'Type', 'Severity', 'Description', 'Actions']}
        headerCta={
          <div className="flex items-center gap-2">
            <ImportCsvButton onClick={() => setImportOpen((v) => !v)} active={importOpen} />
            <NewButton onClick={() => { setEditing(undefined); setEditorOpen(true); }} label="vulnerability" />
          </div>
        }
        rows={q.data.items.map((r) => ({
          key: r.id,
          cells: [
            <span className="font-mono text-ink-500">{r.legacy_code ?? '—'}</span>,
            <span className="text-ink-700 font-medium">{r.name}</span>,
            r.type,
            <RiskInline level={r.severity} />,
            <span className="text-ink-500 text-[10px]">{r.description}</span>,
            <RowActions
              isSystem={!!r.is_system_managed}
              onEdit={() => { setEditing(r); setEditorOpen(true); }}
              onDelete={() => setConfirmDelete(r)}
            />,
          ],
        }))}
      />
      <EntityEditor
        kind="vulnerabilities"
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        existing={editing as ({ id: string } & Record<string, unknown>) | undefined}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete vulnerability"
        message={deleteError
          ? `Failed to delete: ${deleteError}`
          : `This will permanently delete ${confirmDelete?.name}. This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => { setConfirmDelete(null); setDeleteError(null); }}
      />
    </>
  );
}

interface PredisposRow {
  id: string; legacy_code: string | null; name: string; type: string;
  description: string | null; pervasiveness: string;
  is_system_managed: number;
}

function PredisposTable() {
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const q = useQuery({
    queryKey: ['threat-catalog', 'predispos'],
    queryFn: () => api.get<{ items: PredisposRow[]; total: number }>('/threat-catalog/predisposing-conditions'),
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<PredisposRow | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<PredisposRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function doDelete() {
    if (!confirmDelete) return;
    setDeleteError(null);
    try {
      await api.delete(`/admin/threat-catalog/predisposing-conditions/${confirmDelete.id}`);
      qc.invalidateQueries({ queryKey: ['threat-catalog', 'predispos'] });
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;
  if (!q.data) return null;

  return (
    <>
      <CatalogCsvImport
        open={importOpen}
        onOpenChange={setImportOpen}
        entity="predisposing-conditions"
        label="predisposing conditions"
        queryKey={['threat-catalog', 'predispos']}
      />
      <SimpleTable
        total={q.data.total}
        headers={['Code', 'Name', 'Type', 'Pervasiveness', 'Description', 'Actions']}
        headerCta={
          <div className="flex items-center gap-2">
            <ImportCsvButton onClick={() => setImportOpen((v) => !v)} active={importOpen} />
            <NewButton onClick={() => { setEditing(undefined); setEditorOpen(true); }} label="predisposing condition" />
          </div>
        }
        rows={q.data.items.map((r) => ({
          key: r.id,
          cells: [
            <span className="font-mono text-ink-500">{r.legacy_code ?? '—'}</span>,
            <span className="text-ink-700 font-medium">{r.name}</span>,
            r.type,
            <RiskInline level={r.pervasiveness} />,
            <span className="text-ink-500 text-[10px]">{r.description}</span>,
            <RowActions
              isSystem={!!r.is_system_managed}
              onEdit={() => { setEditing(r); setEditorOpen(true); }}
              onDelete={() => setConfirmDelete(r)}
            />,
          ],
        }))}
      />
      <EntityEditor
        kind="predisposing-conditions"
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        existing={editing as ({ id: string } & Record<string, unknown>) | undefined}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete predisposing condition"
        message={deleteError
          ? `Failed to delete: ${deleteError}`
          : `This will permanently delete ${confirmDelete?.name}. This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => { setConfirmDelete(null); setDeleteError(null); }}
      />
    </>
  );
}

interface ImpactRow {
  id: string; legacy_code: string | null; name: string; type: string;
  description: string | null;
  is_system_managed: number;
}

function ImpactsTable() {
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const q = useQuery({
    queryKey: ['threat-catalog', 'impacts'],
    queryFn: () => api.get<{ items: ImpactRow[]; total: number }>('/threat-catalog/impacts'),
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ImpactRow | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<ImpactRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function doDelete() {
    if (!confirmDelete) return;
    setDeleteError(null);
    try {
      await api.delete(`/admin/threat-catalog/impacts/${confirmDelete.id}`);
      qc.invalidateQueries({ queryKey: ['threat-catalog', 'impacts'] });
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;
  if (!q.data) return null;

  return (
    <>
      <CatalogCsvImport
        open={importOpen}
        onOpenChange={setImportOpen}
        entity="impacts"
        label="impacts"
        queryKey={['threat-catalog', 'impacts']}
      />
      <SimpleTable
        total={q.data.total}
        headers={['Code', 'Name', 'Type', 'Description', 'Actions']}
        headerCta={
          <div className="flex items-center gap-2">
            <ImportCsvButton onClick={() => setImportOpen((v) => !v)} active={importOpen} />
            <NewButton onClick={() => { setEditing(undefined); setEditorOpen(true); }} label="impact" />
          </div>
        }
        rows={q.data.items.map((r) => ({
          key: r.id,
          cells: [
            <span className="font-mono text-ink-500">{r.legacy_code ?? '—'}</span>,
            <span className="text-ink-700 font-medium">{r.name}</span>,
            r.type,
            <span className="text-ink-500 text-[10px]">{r.description}</span>,
            <RowActions
              isSystem={!!r.is_system_managed}
              onEdit={() => { setEditing(r); setEditorOpen(true); }}
              onDelete={() => setConfirmDelete(r)}
            />,
          ],
        }))}
      />
      <EntityEditor
        kind="impacts"
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        existing={editing as ({ id: string } & Record<string, unknown>) | undefined}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete impact"
        message={deleteError
          ? `Failed to delete: ${deleteError}`
          : `This will permanently delete ${confirmDelete?.name}. This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => { setConfirmDelete(null); setDeleteError(null); }}
      />
    </>
  );
}

// ============================================================================
// CRQC Probability tab
// ============================================================================

interface CrqcRow {
  year: number; no_of_years_from_start: number;
  min_probability: number; max_probability: number; avg_probability: number;
  source_attribution: string | null; imported_at: string | null;
}

function CrqcProbabilityTab() {
  const readOnly = useAdminReadOnly();
  const q = useQuery({
    queryKey: ['crqc-probability'],
    queryFn: () => api.get<{ items: CrqcRow[]; total: number }>('/admin/crqc-probability'),
  });

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [attribution, setAttribution] = useState('');

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    setImportError(null);

    try {
      const csvText = await file.text();
      const url = apiUrl(`/admin/crqc-probability/import${
        attribution.trim() ? `?attribution=${encodeURIComponent(attribution.trim())}` : ''
      }`);
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv' },
        body: csvText,
        credentials: 'same-origin',
      });
      const text = await res.text();
      const parsed = text ? JSON.parse(text) : null;
      if (!res.ok) {
        const errMsg = parsed?.error?.message ?? `HTTP ${res.status}`;
        setImportError(errMsg);
      } else {
        setImportResult(
          `Imported ${parsed.imported_rows} rows. Rejected: ${parsed.rejected_rows}.` +
          (parsed.source_attribution ? ` Attribution: "${parsed.source_attribution}".` : '')
        );
        // Refetch
        await q.refetch();
      }
    } catch (err) {
      setImportError(err instanceof Error ? err.message : String(err));
    } finally {
      setImporting(false);
      // Reset the file input so the same file can be re-uploaded
      if (e.target) e.target.value = '';
    }
  }

  const isEmpty = q.data && q.data.total === 0;

  return (
    <div>
      <div className="text-xs text-ink-500 mb-4 max-w-3xl">
        Probability timeline for the arrival of a cryptographically relevant
        quantum computer (CRQC). Each project's planning year is read from it
        (the first year the central estimate reaches the project's tolerance,
        default 1 in 3; Settings → Reporting profile). It is
        configured by default with the curve from the{' '}
        <a
          href="https://globalriskinstitute.org/publication/quantum-threat-timeline-report-2025b/"
          className="text-accent underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          GRI Quantum Threat Timeline Report 2025
        </a>{' '}
        {readOnly ? 'and can be replaced by a super user.' : 'and can be replaced with the CSV import below.'}
      </div>

      {/* Import widget */}
      {!readOnly && <div className="border border-ink-200 rounded-sm p-4 mb-6 bg-ink-50/40">
        <h3 className="text-sm font-medium text-ink-900 mb-3">Import CSV</h3>
        <div className="text-xs text-ink-500 mb-3">
          Expected columns:{' '}
          <code className="bg-white border border-ink-200 px-1.5 py-0.5 rounded text-[10px]">
            year, no_of_years_from_start, min_probability, max_probability, avg_probability
          </code>
          . Existing rows are replaced atomically. Lines starting with{' '}
          <code className="bg-white border border-ink-200 px-1.5 py-0.5 rounded text-[10px]">#</code>{' '}
          are treated as comments.
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Attribution (e.g. 'GRI 2025b')"
            value={attribution}
            onChange={(e) => setAttribution(e.target.value)}
            className="border border-ink-200 rounded-sm px-3 py-1 text-sm flex-1 focus:outline-none focus:border-accent"
            disabled={importing}
          />
          <label
            className={`text-sm px-4 py-1 border rounded-sm cursor-pointer transition-colors ${
              importing
                ? 'bg-ink-100 border-ink-200 text-ink-400 cursor-wait'
                : 'bg-white border-ink-300 text-ink-700 hover:bg-accent/5 hover:border-accent'
            }`}
          >
            {importing ? 'Importing…' : 'Choose CSV…'}
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={handleFile}
              disabled={importing}
              className="hidden"
            />
          </label>
        </div>
        {importResult && (
          <div className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2">
            {importResult}
          </div>
        )}
        {importError && (
          <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">
            {importError}
          </div>
        )}
      </div>}

      {/* Current contents */}
      <h3 className="text-sm font-medium text-ink-900 mb-3">Current table contents</h3>
      {q.isLoading && <Loading />}
      {q.isError && <ErrorPanel error={q.error} />}
      {isEmpty && (
        <div className="border border-ink-200 rounded-sm p-6 text-center text-sm text-ink-500 italic">
          The CRQC probability table is empty. Use the import widget above to populate it.
        </div>
      )}
      {q.data && q.data.total > 0 && (
        <div className="border border-ink-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-ink-50 border-b border-ink-200">
              <tr className="text-left text-ink-500 uppercase tracking-wide text-[10px]">
                <th className="px-3 py-2">Year</th>
                <th className="px-3 py-2">Years From Start</th>
                <th className="px-3 py-2">Min %</th>
                <th className="px-3 py-2">Max %</th>
                <th className="px-3 py-2">Avg %</th>
                <th className="px-3 py-2">Attribution</th>
                <th className="px-3 py-2">Imported At</th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((r) => (
                <tr key={r.year} className="border-t border-ink-100">
                  <td className="px-3 py-1.5 font-mono">{r.year}</td>
                  <td className="px-3 py-1.5 font-mono text-ink-500">{r.no_of_years_from_start}</td>
                  <td className="px-3 py-1.5 font-mono">{Number(r.min_probability).toFixed(2)}</td>
                  <td className="px-3 py-1.5 font-mono">{Number(r.max_probability).toFixed(2)}</td>
                  <td className="px-3 py-1.5 font-mono font-medium">{Number(r.avg_probability).toFixed(2)}</td>
                  <td className="px-3 py-1.5 text-ink-500 text-[10px]">{r.source_attribution ?? '—'}</td>
                  <td className="px-3 py-1.5 text-ink-500 text-[10px]">{r.imported_at?.replace('T', ' ').replace('Z', '') ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// License tab — APS entitlement status + activation (air-gap and online)
// ============================================================================

function LicenseTab() {
  const { data: status, isLoading, error, refetch } = useEntitlementStatus();

  if (isLoading) return <Loading />;
  // A build without the licensing service has no entitlement route (404): say so plainly (D15).
  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="max-w-2xl border border-ink-200 bg-ink-50 rounded-sm px-4 py-3 text-sm text-ink-700" data-testid="license-not-enabled">
        <strong className="block text-ink-900 mb-1">Licensing is not enabled in this build</strong>
        This deployment does not enforce a licence, so there is nothing to activate here. Every feature of this build is available.
      </div>
    );
  }
  if (error) return <ErrorPanel error={error} />;
  if (!status) return null;

  return (
    <div className="max-w-2xl space-y-6">
      <LicenseStatusBanner status={status} />

      {status.customer && (
        <dl className="text-sm space-y-2">
          <div className="grid grid-cols-[180px_1fr] gap-2">
            <dt className="text-ink-500">Licensed to</dt>
            <dd className="text-ink-700">{status.customer}</dd>
          </div>
          {status.licenseExpiry && (
            <div className="grid grid-cols-[180px_1fr] gap-2">
              <dt className="text-ink-500">Expires</dt>
              <dd className="text-ink-700 font-mono">
                {new Date(status.licenseExpiry.notAfter * 1000).toISOString().slice(0, 10)}
                {' — '}
                {Math.max(0, Math.floor(status.licenseExpiry.secondsRemaining / 86_400))} day(s) remaining
              </dd>
            </div>
          )}
          {status.connection && (
            <div className="grid grid-cols-[180px_1fr] gap-2">
              <dt className="text-ink-500">Mode</dt>
              <dd className="text-ink-700">{status.connection.mode === 'connected' ? 'Connected' : 'Air-gapped'}</dd>
            </div>
          )}
        </dl>
      )}

      <ActivateScreen onActivated={() => refetch()} />
    </div>
  );
}

// ============================================================================
// About tab
// ============================================================================

function AboutTab() {
  // Slice 9.6.2: pull live version + module list from the backend's
  // /api/v1/system/info endpoint instead of hard-coding values that
  // drift slice by slice. The "v0.7.4" string that lived here was 12+
  // slices stale before this fix.
  const { data, isLoading, error } = useQuery({
    queryKey: ['system-info'],
    queryFn: () => api.get<{
      version: string;
      registered_modules: string[];
      node_env: string;
    }>('/system/info'),
    // Stale time of 5 minutes — the version doesn't change that often
    // and avoids hammering the endpoint on every Admin tab visit.
    staleTime: 5 * 60_000,
  });

  if (isLoading) return <Loading />;
  if (error) return <ErrorPanel error={error} />;
  if (!data) return null;

  const moduleCount = data.registered_modules.length;
  const foundationCount = data.registered_modules.filter((c) =>
    ['timeline', 'data_risk', 'supply_chain', 'migration_feasibility'].includes(c),
  ).length;
  const advancedCount = data.registered_modules.filter((c) =>
    ['threat', 'cryptography', 'compliance'].includes(c),
  ).length;
  const integratedCount = data.registered_modules.filter((c) =>
    c.startsWith('integrated_'),
  ).length;

  return (
    <div className="max-w-2xl">
      <h3 className="text-sm font-medium text-ink-900 mb-3">About QRAM</h3>
      <dl className="space-y-3 text-sm">
        <div className="grid grid-cols-[180px_1fr] gap-2">
          <dt className="text-ink-500">Version</dt>
          <dd className="text-ink-700 font-mono">v{data.version}</dd>
        </div>
        <div className="grid grid-cols-[180px_1fr] gap-2">
          <dt className="text-ink-500">Environment</dt>
          <dd className="text-ink-700 font-mono">{data.node_env}</dd>
        </div>
        <div className="grid grid-cols-[180px_1fr] gap-2">
          <dt className="text-ink-500">Registered modules</dt>
          <dd className="text-ink-700">
            {moduleCount} total · {TIER_LABEL.foundation} ({foundationCount}) · {TIER_LABEL.advanced} ({advancedCount}) · Composite ({integratedCount})
          </dd>
        </div>
        <div className="grid grid-cols-[180px_1fr] gap-2">
          <dt className="text-ink-500">Module list</dt>
          <dd className="text-ink-700 font-mono text-xs">
            {data.registered_modules.join(', ')}
          </dd>
        </div>
        <div className="grid grid-cols-[180px_1fr] gap-2">
          <dt className="text-ink-500">Standards alignment</dt>
          <dd className="text-ink-700">
            NIST SP 800-30 Rev 1 (Threat) · NIST CNSA 2.0 / FIPS 140-3 / NIST FIPS 203, 204, 205 (Compliance Knowledge Base) · NIST IR 8547 (transition to post-quantum cryptography standards) · NIST NCCoE SP 1800-38 (migration practice) · ENISA and ECCG Agreed Cryptographic Mechanisms (hybrid post-quantum)
          </dd>
        </div>
      </dl>
    </div>
  );
}

// ============================================================================
// Shared UI bits
// ============================================================================

function Loading() {
  return <div className="text-sm text-ink-400 italic py-6">Loading…</div>;
}

function ErrorPanel({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <div className="border border-red-200 bg-red-50 text-red-700 rounded-sm p-4 text-sm">
      <div className="font-medium mb-1">Failed to load.</div>
      <div className="text-xs font-mono">{msg}</div>
    </div>
  );
}

function SimpleTable({ total, headers, rows, headerCta }: {
  total: number;
  headers: string[];
  rows: { key: string; cells: React.ReactNode[] }[];
  headerCta?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-ink-500">{rows.length} of {total}</div>
        {headerCta}
      </div>
      <div className="border border-ink-200 rounded-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr className="text-left text-ink-500 uppercase tracking-wide text-[10px]">
              {headers.map((h, i) => (
                <th key={i} className="px-3 py-2">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-ink-100 hover:bg-ink-50/40">
                {r.cells.map((c, i) => (
                  <td key={i} className="px-3 py-2">{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RowActions({ isSystem, onEdit, onDelete }: {
  isSystem: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const readOnly = useAdminReadOnly();
  if (readOnly) return null;
  if (isSystem) {
    return (
      <span className="text-[10px] text-ink-400 italic">system-managed</span>
    );
  }
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onEdit}
        className="text-[11px] text-accent hover:underline"
      >
        Edit
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="text-[11px] text-red-700 hover:underline"
      >
        Delete
      </button>
    </div>
  );
}

function NewButton({ onClick, label }: { onClick: () => void; label: string }) {
  const readOnly = useAdminReadOnly();
  if (readOnly) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm px-3 py-1 bg-accent text-white border border-accent rounded-sm hover:bg-accent/90"
    >
      + New {label}
    </button>
  );
}

// Risk badge inline (small variant, for tables)
function RiskInline({ level }: { level: string }) {
  const tone = (() => {
    switch (level) {
      case 'No_risk':   return 'bg-ink-50 text-ink-500 border-ink-200';
      case 'Very_low':  return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Low':       return 'bg-emerald-50 text-emerald-800 border-emerald-300';
      case 'Moderate':  return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'High':      return 'bg-orange-50 text-orange-800 border-orange-300';
      case 'Very_high': return 'bg-red-50 text-red-800 border-red-300';
      case 'Critical':  return 'bg-red-100 text-red-900 border-red-400';
      default:          return 'bg-ink-50 text-ink-500 border-ink-200';
    }
  })();
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm border ${tone}`}>
      {level.replace('_', ' ')}
    </span>
  );
}